/**
 * Encounter Detail Panel
 *
 * Enterprise slide-over panel for displaying comprehensive encounter information.
 * Opens from the right side with smooth animation.
 * Provides direct "Start Recording" navigation for connected mode.
 * Refined visual hierarchy with section cards and consistent status badges.
 */

import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  User,
  Stethoscope,
  UserCircle,
  Database,
  Mic,
  Info,
  Calendar,
  MapPin,
  FileText,
  ClipboardCheck,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Send,
  Clock
} from 'lucide-react';
import { Encounter } from '@/types/encounter';
import { ENCOUNTER_STATUS_STYLES } from '@/components/encounters/types';
import { recordingService } from '@/services/recording';
import { useEhrRegistry } from '@/hooks/useEhrRegistry';
import { EHR_PRE_LOCALIZED_SYSTEMS, formatEhrDateTime, isSentinelDate } from '@/utils/dateFormat';

// ============================================================================
// Status Badge (consistent with table/card design)
// ============================================================================

const MATCH_STYLES: Record<string, { text: string; bg: string; Icon: typeof CheckCircle }> = {
  matched: { text: 'text-emerald-700', bg: 'bg-emerald-50', Icon: CheckCircle },
  unmatched: { text: 'text-red-700', bg: 'bg-red-50', Icon: AlertTriangle },
  pending: { text: 'text-amber-700', bg: 'bg-amber-50', Icon: Clock },
};

const STATUS_ICONS = { sent: Send, alert: AlertCircle };

function PanelStatusBadge({ status }: { status: string }) {
  const config = ENCOUNTER_STATUS_STYLES[status] || ENCOUNTER_STATUS_STYLES.scheduled;
  const Icon = config.icon && STATUS_ICONS[config.icon];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bg} ${config.text}`}>
      {Icon ? <Icon className="h-3.5 w-3.5" /> : <span className={`w-2 h-2 rounded-full ${config.dot}`} />}
      {config.label}
    </span>
  );
}

function MatchStatusBadge({ status }: { status: string }) {
  const config = MATCH_STYLES[status];
  if (!config) return null;
  const { Icon, text, bg } = config;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="h-3.5 w-3.5" />
      {status}
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface EncounterDetailPanelProps {
  encounter: Encounter | null;
  isOpen: boolean;
  onClose: () => void;
}

const EncounterDetailPanel: React.FC<EncounterDetailPanelProps> = ({
  encounter,
  isOpen,
  onClose
}) => {
  const navigate = useNavigate();
  const { labelFor } = useEhrRegistry();
  const [isNavigating, setIsNavigating] = useState(false);
  const [appointmentIdForReview, setAppointmentIdForReview] = useState<string | null>(null);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [isTranscriptionInProgress, setIsTranscriptionInProgress] = useState(false);
  const [isTranscribed, setIsTranscribed] = useState(false);

  useEffect(() => {
    setAppointmentIdForReview(null);
    setIsTranscriptionInProgress(false);
    setIsTranscribed(false);
    if (!encounter) return;

    const fetchRecordingData = async () => {
      try {
        setIsLoadingRecording(true);
        console.log('Checking if encounter has recording:', encounter.id);

        const result = await recordingService.getRecordings({
          encounterId: encounter.id,
          limit: 1
        });

        const recording = result.recordings[0];
        // The inline fast-speech path holds the recording at 'uploaded' for the whole
        // transcription and never reaches 'processing', so both count as in flight.
        setIsTranscriptionInProgress(
          !!recording && (recording.status === 'uploaded' || recording.status === 'processing')
        );
        // The list's copy of the encounter is loaded once and never refreshed, so a
        // transcription that finished after page load only shows up on the recording.
        setIsTranscribed(recording?.status === 'transcribed');

        if (recording && (encounter.encounterDetails.status === 'completed' || recording.status === 'transcribed')) {
          setAppointmentIdForReview(encounter.id);
        }
      } catch (error) {
        console.error('Failed to fetch recording data:', error);
      } finally {
        setIsLoadingRecording(false);
      }
    };

    fetchRecordingData();
  }, [encounter]);

  const handleStartRecording = () => {
    if (!encounter) return;
    setIsNavigating(true);
    onClose();
    navigate(`/recording?encounterId=${encounter.id}`);
  };

  const handleReviewClinicalNote = () => {
    if (!encounter) return;
    setIsNavigating(true);
    onClose();
    navigate(`/encounter/${encounter.id}/validation`);
  };

  const isRecordingDisabled = () => {
    if (!encounter) return true;
    return isTranscriptionInProgress ||
           isTranscribed ||
           encounter.encounterDetails.status === 'completed' ||
           encounter.encounterDetails.status === 'cancelled';
  };

  const getDisabledTooltip = () => {
    if (!encounter) return '';
    if (isTranscriptionInProgress) {
      return 'This encounter is being transcribed';
    }
    if (isTranscribed || encounter.encounterDetails.status === 'completed') {
      return 'This encounter has already been recorded';
    }
    if (encounter.encounterDetails.status === 'cancelled') {
      return `This encounter was cancelled in ${labelFor(encounter.externalSystemId)}`;
    }
    return '';
  };

  const formatDOB = (dob?: string) => {
    if (!dob || isSentinelDate(dob)) return 'Not Available';
    const date = new Date(dob);
    if (isNaN(date.getTime())) return 'Not Available';
    const age = Math.floor((Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return `Born ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (${age} years old)`;
  };

  const formatScheduledDate = (dateString: string) => {
    const date = new Date(dateString);
    const isPreLocalized = encounter && EHR_PRE_LOCALIZED_SYSTEMS.has(encounter.externalSystemId);

    if (isPreLocalized) {
      return formatEhrDateTime(dateString);
    }

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (isToday) return `Today at ${timeStr}`;
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${timeStr}`;
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
           ' at ' +
           date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const isScheduledToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const isPreLocalized = encounter && EHR_PRE_LOCALIZED_SYSTEMS.has(encounter.externalSystemId);

    if (isPreLocalized) {
      return date.getUTCFullYear() === today.getFullYear()
        && date.getUTCMonth() === today.getMonth()
        && date.getUTCDate() === today.getDate();
    }

    return date.toDateString() === today.toDateString();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px]" />
        </Transition.Child>

        {/* Slide-over panel */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-clinical-strong">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5 z-10">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-bold text-gray-900">
                          Encounter Details
                        </Dialog.Title>
                        <button
                          onClick={onClose}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          aria-label="Close encounter details"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Action Buttons */}
                      {encounter && (
                        <div className="mt-4 space-y-2">
                          {(encounter.encounterDetails.status === 'completed' || isTranscribed) && appointmentIdForReview && !isTranscriptionInProgress && (
                            <button
                              onClick={handleReviewClinicalNote}
                              disabled={isNavigating || isLoadingRecording}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Review and validate the clinical note"
                            >
                              <ClipboardCheck className="w-5 h-5" />
                              {isNavigating ? 'Loading...' : 'Review Clinical Note'}
                            </button>
                          )}

                          <button
                            onClick={handleStartRecording}
                            disabled={isRecordingDisabled() || isNavigating || isLoadingRecording}
                            title={isRecordingDisabled() ? getDisabledTooltip() : 'Start recording this encounter'}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Mic className="w-5 h-5" />
                            {isNavigating ? 'Loading...' : isTranscriptionInProgress ? 'Transcription in progress' : 'Start Recording'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Content - Scrollable */}
                    {encounter ? (
                      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                        {/* Patient Section */}
                        <DetailSection title="Patient Information" icon={User}>
                          <div className="space-y-3">
                            <DetailField
                              label="Name"
                              value={`${encounter.patientData.firstName} ${encounter.patientData.lastName}`}
                            />
                            <DetailField
                              label="Date of Birth"
                              value={formatDOB(encounter.patientData.dateOfBirth)}
                            />
                            <DetailField
                              label="MRN"
                              value={encounter.patientData.mrn || 'Not provided'}
                              badge={!!encounter.patientData.mrn}
                            />
                            <DetailField
                              label="Gender"
                              value={encounter.patientData.gender || 'Not provided'}
                            />
                          </div>
                        </DetailSection>

                        {/* Encounter Section */}
                        <DetailSection title="Encounter Details" icon={Stethoscope}>
                          <div className="space-y-3">
                            <DetailField
                              label="Type"
                              value={encounter.encounterDetails.type}
                              icon={FileText}
                            />
                            <DetailField
                              label="Scheduled"
                              value={formatScheduledDate(encounter.encounterDetails.scheduledDate)}
                              icon={Calendar}
                              highlight={isScheduledToday(encounter.encounterDetails.scheduledDate)}
                            />
                            {encounter.encounterDetails.location && (
                              <DetailField
                                label="Location"
                                value={encounter.encounterDetails.location}
                                icon={MapPin}
                              />
                            )}
                            <DetailField
                              label="Chief Complaint"
                              value={encounter.encounterDetails.chiefComplaint || 'Not provided'}
                              expandable={!!encounter.encounterDetails.chiefComplaint && encounter.encounterDetails.chiefComplaint.length > 100}
                            />
                            <DetailField
                              label="Status"
                              value={<PanelStatusBadge status={encounter.displayStatus ?? encounter.encounterDetails.status} />}
                            />
                          </div>
                        </DetailSection>

                        {/* Provider Section */}
                        <DetailSection title="Provider" icon={UserCircle}>
                          <div className="space-y-3">
                            <DetailField
                              label="Assigned To"
                              value={encounter.assignedUserId ? 'You' : 'Unassigned'}
                            />
                            <DetailField
                              label="External Provider ID"
                              value={encounter.externalProviderId}
                              badge
                            />
                          </div>
                        </DetailSection>

                        {/* System Section */}
                        <DetailSection title="System Information" icon={Database}>
                          <div className="space-y-3">
                            <DetailField
                              label="EHR System"
                              value={labelFor(encounter.externalSystemId)}
                            />
                            <DetailField
                              label="Last Synced"
                              value={formatTimestamp(encounter.updatedAt)}
                            />
                            <DetailField
                              label="Match Status"
                              value={<MatchStatusBadge status={encounter.matchStatus} />}
                            />

                            {/* Read-only disclaimer */}
                            <div className="mt-4 p-3.5 bg-blue-50 rounded-xl flex items-start gap-2.5">
                              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-blue-700 leading-relaxed">
                                This data is managed in {labelFor(encounter.externalSystemId)} and cannot be edited here.
                              </p>
                            </div>
                          </div>
                        </DetailSection>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                            <Stethoscope className="w-6 h-6 text-gray-400" />
                          </div>
                          <p className="text-gray-500 text-sm">No encounter selected</p>
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                      <button
                        onClick={onClose}
                        className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// ============================================================================
// Helper Components
// ============================================================================

const DetailSection: React.FC<{
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-4">
      <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      {title}
    </h3>
    {children}
  </div>
);

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  badge?: boolean;
  expandable?: boolean;
  highlight?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}> = ({ label, value, badge, expandable, highlight, icon: Icon }) => {
  const [expanded, setExpanded] = useState(false);

  const shouldTruncate = expandable && typeof value === 'string' && value.length > 100;

  return (
    <div className={`${highlight ? 'bg-blue-50/80 -mx-1 px-3 py-2.5 rounded-lg border border-blue-100' : ''}`}>
      <dt className="text-[11px] font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </dt>
      <dd className={`mt-1 text-sm text-gray-900 ${badge ? 'inline-flex' : ''}`}>
        {shouldTruncate ? (
          <div>
            <span>{expanded ? value : (value as string).slice(0, 100) + '...'}</span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-2 text-blue-600 hover:text-blue-700 font-medium text-xs"
            >
              {expanded ? 'Read less' : 'Read more'}
            </button>
          </div>
        ) : badge && typeof value === 'string' ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-mono font-medium bg-gray-100 text-gray-700 border border-gray-200">
            {value}
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
};

export default EncounterDetailPanel;
